/**
 * Statut d'une période d'immersion — source unique de vérité.
 *
 * Le statut affiché ne vient pas de la base : `Immersion.statut` y porte le
 * cycle de décision (`PLANIFIEE`, `EN_COURS`, `VALIDEE`, `NON_VALIDEE`) et peut
 * être en retard sur les dates — une immersion planifiée le mois dernier reste
 * `PLANIFIEE` tant que personne ne l'a touchée. L'écran, lui, doit dire où en
 * est la période *aujourd'hui*. Il le déduit donc des dates, et un seul endroit
 * le fait : badge, filtres, compteurs et fiche détail appellent tous
 * `getImmersionStatus`, faute de quoi un badge « En cours » finit par ne rien
 * rendre au filtre « En cours ».
 *
 * Les couleurs suivent la même règle : elles vivent ici, pas dans les écrans.
 */

import { aujourdhui } from './dates-civiles';

/** Statut calculé à partir des dates. Ce n'est pas l'enum Prisma. */
export type StatutImmersionCalcule = 'A_VENIR' | 'EN_COURS' | 'TERMINEE';

/** Statut enregistré en base (cycle de décision), distinct du calculé. */
export type StatutImmersionEnregistre = 'PLANIFIEE' | 'EN_COURS' | 'VALIDEE' | 'NON_VALIDEE';

/**
 * Couleur et libellé de chaque statut. Un statut, une couleur, partout.
 * Les tons correspondent aux classes `badge-*` de `index.css`.
 */
export const LIBELLES_IMMERSION: Record<
  StatutImmersionCalcule,
  { texte: string; ton: string; classeFiltreActif: string }
> = {
  A_VENIR: { texte: 'À venir', ton: 'info', classeFiltreActif: 'bg-[#dbeafe] text-[#1e40af]' },
  EN_COURS: { texte: 'En cours', ton: 'warning', classeFiltreActif: 'bg-[#fef3c7] text-[#92400e]' },
  TERMINEE: { texte: 'Terminée', ton: 'success', classeFiltreActif: 'bg-[#d1fae5] text-[#065f46]' },
};

/** Ordre d'affichage des filtres et des compteurs. */
export const STATUTS_IMMERSION: StatutImmersionCalcule[] = ['A_VENIR', 'EN_COURS', 'TERMINEE'];

/**
 * Jour civil réunionnais d'un instant, ramené à un entier `AAAAMMJJ`.
 *
 * Comparer des `Date` avec `setHours(0,0,0,0)` raisonne dans le fuseau du poste,
 * qui n'est pas forcément celui de La Réunion : un début d'immersion enregistré
 * à `2026-09-20T00:00:00Z` retombe au 19 sur un poste en UTC-5 et l'immersion
 * paraît « à venir » un jour de trop. On compare donc des jours civils
 * `Indian/Reunion`, jamais des instants.
 */
function jourCivil(valeur: string | null): number | null {
  if (!valeur) return null;
  const instant = new Date(valeur);
  if (Number.isNaN(instant.getTime())) return null;
  const civile = new Intl.DateTimeFormat('en-CA', { timeZone: 'Indian/Reunion' }).format(instant);
  return Number(civile.replace(/-/g, ''));
}

function jourCivilDuJour(): number {
  return Number(aujourdhui().replace(/-/g, ''));
}

/**
 * Statut d'une immersion au jour d'aujourd'hui.
 *
 * - début après aujourd'hui → `A_VENIR` ;
 * - fin avant aujourd'hui → `TERMINEE` ;
 * - le reste → `EN_COURS`.
 *
 * Les deux bornes sont **incluses** : une immersion qui commence aujourd'hui est
 * en cours, et une immersion qui finit aujourd'hui l'est encore — la dernière
 * journée se travaille, elle ne bascule à `TERMINEE` que le lendemain.
 *
 * Une date illisible ne fait pas planter l'écran : la borne manquante est
 * ignorée, et une immersion sans date exploitable est dite en cours plutôt que
 * disparaître des trois filtres.
 */
export function getImmersionStatus(immersion: {
  dateDebut: string | null;
  dateFin: string | null;
}): StatutImmersionCalcule {
  const jour = jourCivilDuJour();
  const debut = jourCivil(immersion.dateDebut);
  const fin = jourCivil(immersion.dateFin);

  if (debut !== null && debut > jour) return 'A_VENIR';
  if (fin !== null && fin < jour) return 'TERMINEE';
  return 'EN_COURS';
}

/** Libellé et ton d'une immersion, sans recalcul dispersé dans les écrans. */
export function libelleImmersion(immersion: { dateDebut: string | null; dateFin: string | null }): {
  texte: string;
  ton: string;
} {
  return LIBELLES_IMMERSION[getImmersionStatus(immersion)];
}

/**
 * Pastille d'un positionnement : sa réponse quand elle est donnée, sinon où en
 * est la période aujourd'hui. Un positionnement sans immersion n'a pas de
 * période : il le dit.
 */
export function libellePositionnement(immersion: {
  dateDebut: string | null;
  dateFin: string | null;
  sansImmersion: boolean;
  statut: StatutImmersionEnregistre;
}): { texte: string; ton: string } {
  if (immersion.sansImmersion) return { texte: 'Sans immersion', ton: 'mint' };
  if (immersion.statut === 'VALIDEE') return { texte: 'Validée · oui', ton: 'success' };
  if (immersion.statut === 'NON_VALIDEE') return { texte: 'Validée · non', ton: 'danger' };
  return libelleImmersion(immersion);
}

/** Compteur par statut, calculé exactement comme les filtres les appliquent. */
export function compterParStatut<T extends { dateDebut: string | null; dateFin: string | null }>(
  immersions: T[],
): Record<StatutImmersionCalcule, number> {
  const compteurs: Record<StatutImmersionCalcule, number> = {
    A_VENIR: 0,
    EN_COURS: 0,
    TERMINEE: 0,
  };
  for (const immersion of immersions) {
    compteurs[getImmersionStatus(immersion)] += 1;
  }
  return compteurs;
}

/**
 * Immersion à traiter : terminée, sans compte rendu d'entretien.
 *
 * C'est le seul cas où la période est close et où il reste quelque chose à
 * faire — la relation entreprise doit pouvoir le repérer sans relire toute la
 * liste. Une immersion en cours n'est pas en retard : le compte rendu se fait
 * pendant la visite, pas avant.
 */
export function immersionATraiter(immersion: {
  dateDebut: string | null;
  dateFin: string | null;
  compteRendu: unknown;
}): boolean {
  return getImmersionStatus(immersion) === 'TERMINEE' && !immersion.compteRendu;
}

/** Période d'un positionnement, ou l'absence d'immersion quand il n'en a pas. */
export function periodePositionnement(
  immersion: { dateDebut: string | null; dateFin: string | null; sansImmersion: boolean },
  formater: (valeur: string) => string,
): string {
  if (immersion.sansImmersion || !immersion.dateDebut || !immersion.dateFin) {
    return 'Sans immersion';
  }
  return `Du ${formater(immersion.dateDebut)} au ${formater(immersion.dateFin)}`;
}
